/**
 * Per-hanke upcoming-deadline summary for Etusivu.
 *
 * One deadline-sorted query covers both numbers the project cards need: how
 * many unfinished top-level tasks fall due between today and the end of the
 * current week, and which task comes next. Because the query is sorted by
 * määräaika ascending, the week's tasks always sit at the front of the window,
 * so the week count only degrades if more than `WINDOW` tasks share it.
 */

import { useMemo } from "react";
import { useQuery } from "@apollo/client";
import { GET_TASKS, buildGraphQLFilter } from "./useTasks";
import { TaskStatus } from "@/types";
import type { TaskExtended } from "@/types";

const WINDOW = 200;

const UNFINISHED: TaskStatus[] = [
  TaskStatus.PENDING,
  TaskStatus.IN_PROGRESS,
  TaskStatus.REJECTED,
  TaskStatus.CANNOT_MEASURE,
];

export interface UpcomingSummary {
  dueThisWeek: number;
  next: { title: string; dueDate: string } | null;
}

export function useUpcomingProjectTasks(
  projectIds: string[],
  fromISO: string,
  weekEndISO: string,
): { byProject: Map<string, UpcomingSummary>; loading: boolean } {
  const filter = useMemo(
    () =>
      buildGraphQLFilter({
        projectIds,
        statuses: UNFINISHED,
        dueAfter: fromISO,
        sortBy: "deadline",
        sortDirection: "asc",
        parentIdIsNull: true,
      }),
    [projectIds, fromISO],
  );

  const { data, loading } = useQuery<{ tasks: { items: TaskExtended[] } }>(
    GET_TASKS,
    {
      variables: { limit: WINDOW, filter },
      skip: projectIds.length === 0,
      fetchPolicy: "cache-and-network",
    },
  );

  const byProject = useMemo(() => {
    const weekEndMs = new Date(weekEndISO).getTime();
    const map = new Map<string, UpcomingSummary>();
    for (const task of data?.tasks?.items ?? []) {
      if (!task.projectId || !task.dueDate) continue;
      const entry = map.get(task.projectId) ?? { dueThisWeek: 0, next: null };
      if (!entry.next) entry.next = { title: task.title, dueDate: task.dueDate };
      if (new Date(task.dueDate).getTime() <= weekEndMs) entry.dueThisWeek += 1;
      map.set(task.projectId, entry);
    }
    return map;
  }, [data, weekEndISO]);

  return { byProject, loading };
}
